import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../../context/AuthContext';
import {
  Box,
  Button,
  TextField,
  Typography,
  Link,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Container,
  Paper,
  Fade
} from '@mui/material';
import { Visibility, VisibilityOff, CheckCircleOutline, LockOutlined } from '@mui/icons-material';
import { login, checkEmailExists, sendEmailOTP } from '../../api/authApi';
import EmailVerifyDialog from './EmailVerifyDialog';
import PasswordStrengthIndicator from './PasswordStrengthIndicator';
import AuthBlueprintHero, { AuthBlueprintPanel } from './AuthBlueprintHero';

// Your original theme color
const themeColor = 'rgb(57, 170, 252)'; // #39aafc

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
  password: Yup.string()
    .required('Password is required')
});

const forgotPasswordSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required')
});

const newPasswordSchema = Yup.object().shape({
  newPassword: Yup.string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(/[A-Z]/, 'Must contain at least one uppercase letter')
    .matches(/[0-9]/, 'Must contain at least one number')
    .matches(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/, 'Must contain at least one special character'),
  confirmPassword: Yup.string()
    .required('Please confirm your password')
    .oneOf([Yup.ref('newPassword')], 'Passwords must match')
});

// Clean MUI input styles with Roboto font
const inputFieldStyles = {
  mb: 2.5,
  '& .MuiOutlinedInput-root': {
    fontFamily: 'Roboto, sans-serif',
    backgroundColor: '#fff',
    borderRadius: '14px',
    transition: 'box-shadow 0.25s ease',
    '& fieldset': {
      borderColor: '#e3e8ef',
    },
    '&:hover fieldset': {
      borderColor: themeColor,
    },
    '&.Mui-focused': {
      boxShadow: `0 0 0 4px ${themeColor}1f`,
    },
    '&.Mui-focused fieldset': {
      borderColor: themeColor,
      borderWidth: '1.5px',
    },
  },
  '& .MuiInputBase-input': {
    fontSize: '16px', // 16px prevents iOS from auto-zooming on focus
    padding: '15px 14px',
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'Roboto, sans-serif',
    '&.Mui-focused': {
      color: themeColor,
    },
  },
  '& .MuiFormHelperText-root': {
    fontFamily: 'Roboto, sans-serif',
  },
};

// Shared gradient primary button (tactile press + glow)
const primaryButtonSx = {
  py: 1.6,
  fontFamily: 'Roboto, sans-serif',
  fontSize: '16px',
  fontWeight: 700,
  mb: 2,
  borderRadius: '14px',
  color: '#fff',
  textTransform: 'none',
  background: `linear-gradient(135deg, ${themeColor} 0%, #1565C0 100%)`,
  boxShadow: `0 10px 24px ${themeColor}59`,
  transition: 'transform 0.15s ease, box-shadow 0.25s ease',
  '&:hover': {
    background: `linear-gradient(135deg, ${themeColor} 0%, #1565C0 100%)`,
    boxShadow: `0 12px 30px ${themeColor}80`,
  },
  '&:active': {
    transform: 'scale(0.98)',
  },
  '&.Mui-disabled': {
    color: '#fff',
    opacity: 0.75,
  },
};

const LoginPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { login: setAuthUser } = useAuth();

  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Password reset states
  const [isResetPasswordMode, setIsResetPasswordMode] = useState(false);
  const [resetPasswordEmail, setResetPasswordEmail] = useState('');
  const [showPasswordResetOtpDialog, setShowPasswordResetOtpDialog] = useState(false);
  const [showNewPasswordFields, setShowNewPasswordFields] = useState(false);
  const [userId, setUserId] = useState(null);
  const [isPasswordFieldActive, setIsPasswordFieldActive] = useState(false);

  // Check for password reset success message
  useEffect(() => {
    const message = localStorage.getItem('passwordResetSuccess');
    if (message) {
      setSuccessMessage(message);
      localStorage.removeItem('passwordResetSuccess');
      setTimeout(() => setSuccessMessage(null), 5000);
    }
  }, []);

  const togglePassword = () => setShowPassword(!showPassword);
  const toggleResetPasswordMode = () => {
    setIsResetPasswordMode(!isResetPasswordMode);
    setError(null);
    setSuccessMessage(null);
    setShowNewPasswordFields(false);
    setResetPasswordEmail('');
  };

  // Handle login
  const handleLoginSubmit = async (values, { setFieldError }) => {
    try {
      setSubmitting(true);
      setError(null);

      const response = await login(values);

      if (response.success) {
        // Store user data in AuthContext
        setAuthUser(response.user);
        navigate('/');
      }
    } catch (err) {
      if (err.accountLocked) {
        setError('Account locked due to multiple failed login attempts. Please check your email for unlock instructions.');
      } else {
        setError(err.message || 'Invalid email or password. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Handle forgot password - Step 1: Email entry
  const handleForgotPasswordSubmit = async (values, { setFieldError }) => {
    try {
      setSubmitting(true);
      setError(null);

      const response = await checkEmailExists({ email: values.email });

      if (!response.exists) {
        setFieldError('email', 'This email is not registered with us');
        return;
      }

      await sendEmailOTP({
        email: values.email,
        purpose: 'password_reset'
      });

      setResetPasswordEmail(values.email);
      setUserId(response.userId);
      setShowPasswordResetOtpDialog(true);
    } catch (err) {
      setError(err.message || 'Failed to send OTP. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle OTP verified - Step 2: Show new password form
  const handlePasswordResetOtpVerified = () => {
    setShowPasswordResetOtpDialog(false);
    setShowNewPasswordFields(true);
  };

  // Handle new password submission - Step 3: Reset password
  const handleNewPasswordSubmit = async (values) => {
    try {
      setSubmitting(true);
      setError(null);

      const { resetPassword } = await import('../../api/authApi');
      await resetPassword({
        email: resetPasswordEmail,
        userId: userId,
        password: values.newPassword
      });

      localStorage.setItem('passwordResetSuccess',
        'Your password has been successfully reset. You can now log in with your new password.');

      window.location.href = '/login';
    } catch (err) {
      setError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: { xs: 2, sm: 3 },
        pt: { xs: 'max(16px, env(safe-area-inset-top))', sm: 3 },
        pb: { xs: 'max(16px, env(safe-area-inset-bottom))', sm: 3 },
        background: {
          xs: 'linear-gradient(160deg, #081b3a 0%, #0d2b5c 55%, #123a75 100%)',
          sm: 'linear-gradient(160deg, #0d47a1 0%, #1565C0 32%, #42A5F5 68%, #90CAF9 100%)',
        },
        position: 'relative',
        overflow: 'hidden',
        // Floating aurora orb (top-left)
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '-15%',
          left: '-20%',
          width: '70vw',
          height: '70vw',
          maxWidth: '520px',
          maxHeight: '520px',
          borderRadius: '50%',
          background: { xs: 'radial-gradient(circle, rgba(77,178,252,0.18) 0%, rgba(66,165,245,0) 70%)', sm: 'radial-gradient(circle, rgba(255,255,255,0.35) 0%, rgba(66,165,245,0) 70%)' },
          filter: 'blur(20px)',
          animation: 'floatA 14s ease-in-out infinite',
        },
        // Floating aurora orb (bottom-right)
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: '-20%',
          right: '-15%',
          width: '80vw',
          height: '80vw',
          maxWidth: '560px',
          maxHeight: '560px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(13,71,161,0.45) 0%, rgba(13,71,161,0) 70%)',
          filter: 'blur(20px)',
          animation: 'floatB 18s ease-in-out infinite',
        },
        '@keyframes floatA': {
          '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
          '50%': { transform: 'translate(30px, 40px) scale(1.08)' },
        },
        '@keyframes floatB': {
          '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
          '50%': { transform: 'translate(-40px, -30px) scale(1.12)' },
        },
      }}
    >
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1, py: { xs: 1, sm: 4 }, px: { xs: 0, sm: 2 }, maxWidth: { md: '960px' } }}>
        <Fade in={true} timeout={800}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: { xs: '28px', sm: '24px' },
              overflow: 'hidden',
              background: 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.6)',
              boxShadow: '0 24px 70px rgba(8, 47, 121, 0.35), 0 2px 8px rgba(0, 0, 0, 0.08)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
              <Box sx={{ display: { xs: 'none', md: 'block' }, width: '42%', flexShrink: 0 }}>
                <AuthBlueprintPanel />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Logo Section */}
            {isMobile && <AuthBlueprintHero />}
            <Box
              sx={{
                pt: { xs: isMobile ? 3 : 5, sm: 6 },
                pb: 3,
                px: { xs: 3, sm: 4 },
                textAlign: 'center',
                background: `linear-gradient(135deg, ${themeColor}08 0%, transparent 100%)`,
              }}
            >
              {!isResetPasswordMode && !showNewPasswordFields && (
                <>
                  <Typography
                    variant="h4"
                    sx={{
                      fontFamily: 'Roboto, sans-serif',
                      fontWeight: 700,
                      color: '#1a1a1a',
                      mb: 1,
                      fontSize: { xs: '24px', sm: '28px' },
                    }}
                  >
                    Welcome Back
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontFamily: 'Roboto, sans-serif',
                      color: '#666',
                      fontSize: '15px',
                    }}
                  >
                    Sign in to continue to your account
                  </Typography>
                </>
              )}

              {isResetPasswordMode && !showNewPasswordFields && (
                <>
                  <Typography
                    variant="h4"
                    sx={{
                      fontFamily: 'Roboto, sans-serif',
                      fontWeight: 700,
                      color: '#1a1a1a',
                      mb: 1,
                      fontSize: { xs: '24px', sm: '28px' },
                    }}
                  >
                    Reset Password
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontFamily: 'Roboto, sans-serif',
                      color: '#666',
                      fontSize: '15px',
                    }}
                  >
                    Enter your email to receive a verification code
                  </Typography>
                </>
              )}

              {showNewPasswordFields && (
                <>
                  <Typography
                    variant="h4"
                    sx={{
                      fontFamily: 'Roboto, sans-serif',
                      fontWeight: 700,
                      color: '#1a1a1a',
                      mb: 1,
                      fontSize: { xs: '24px', sm: '28px' },
                    }}
                  >
                    Create New Password
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontFamily: 'Roboto, sans-serif',
                      color: '#666',
                      fontSize: '15px',
                    }}
                  >
                    Enter your new password below
                  </Typography>
                </>
              )}
            </Box>

            {/* Form Section */}
            <Box sx={{ px: { xs: 3, sm: 4 }, pb: { xs: 5, sm: 6 } }}>
              {/* Success Message */}
              {successMessage && (
                <Alert
                  severity="success"
                  sx={{
                    mb: 3,
                    borderRadius: '12px',
                    border: '1px solid #4caf5020',
                  }}
                >
                  {successMessage}
                </Alert>
              )}

              {/* Error Message */}
              {error && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    borderRadius: '12px',
                    border: '1px solid #f4433620',
                  }}
                >
                  {error}
                </Alert>
              )}

              {/* Login Form */}
              {!isResetPasswordMode && !showNewPasswordFields && (
                <Formik
                  initialValues={{ email: '', password: '' }}
                  validationSchema={loginSchema}
                  onSubmit={handleLoginSubmit}
                >
                  {({ errors, touched }) => (
                    <Form>
                      <Field name="email">
                        {({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Email Address"
                            type="email"
                            error={touched.email && Boolean(errors.email)}
                            helperText={touched.email && errors.email}
                            sx={inputFieldStyles}
                          />
                        )}
                      </Field>

                      <Field name="password">
                        {({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Password"
                            type={showPassword ? 'text' : 'password'}
                            error={touched.password && Boolean(errors.password)}
                            helperText={touched.password && errors.password}
                            sx={inputFieldStyles}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={togglePassword}
                                    edge="end"
                                    sx={{ color: '#666' }}
                                  >
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      </Field>

                      <Box sx={{ textAlign: 'right', mb: 3 }}>
                        <Link
                          component="button"
                          type="button"
                          variant="body2"
                          onClick={toggleResetPasswordMode}
                          sx={{
                            fontFamily: 'Roboto, sans-serif',
                            color: themeColor,
                            textDecoration: 'none',
                            fontWeight: 600,
                            fontSize: '14px',
                            '&:hover': {
                              textDecoration: 'underline',
                            },
                          }}
                        >
                          Forgot password?
                        </Link>
                      </Box>

                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={submitting}
                        sx={primaryButtonSx}
                      >
                        {submitting ? (
                          <CircularProgress size={24} sx={{ color: 'white' }} />
                        ) : (
                          'Sign In'
                        )}
                      </Button>

                      <Box
                        sx={{
                          textAlign: 'center',
                          mt: 3,
                          pt: 3,
                          borderTop: '1px solid #e0e0e0',
                        }}
                      >
                        <Typography variant="body2" sx={{ fontFamily: 'Roboto, sans-serif', color: '#666', fontSize: '14px' }}>
                          Don't have an account?{' '}
                          <Link
                            component={RouterLink}
                            to="/register"
                            sx={{
                              fontFamily: 'Roboto, sans-serif',
                              color: themeColor,
                              fontWeight: 700,
                              textDecoration: 'none',
                              '&:hover': {
                                textDecoration: 'underline',
                              },
                            }}
                          >
                            Create An Account
                          </Link>
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'Roboto, sans-serif', color: '#666', fontSize: '14px', mt: 1.5 }}>
                          First Time Sub-User Login?{' '}
                          <Link
                            component={RouterLink}
                            to="/sub-user-login"
                            sx={{
                              fontFamily: 'Roboto, sans-serif',
                              color: themeColor,
                              fontWeight: 700,
                              textDecoration: 'none',
                              '&:hover': {
                                textDecoration: 'underline',
                              },
                            }}
                          >
                            Login
                          </Link>
                        </Typography>
                      </Box>
                    </Form>
                  )}
                </Formik>
              )}

              {/* Forgot Password Form */}
              {isResetPasswordMode && !showNewPasswordFields && (
                <Formik
                  initialValues={{ email: '' }}
                  validationSchema={forgotPasswordSchema}
                  onSubmit={handleForgotPasswordSubmit}
                >
                  {({ errors, touched }) => (
                    <Form>
                      <Field name="email">
                        {({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Email Address"
                            type="email"
                            error={touched.email && Boolean(errors.email)}
                            helperText={touched.email && errors.email}
                            sx={inputFieldStyles}
                          />
                        )}
                      </Field>

                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={submitting}
                        sx={primaryButtonSx}
                      >
                        {submitting ? (
                          <CircularProgress size={24} sx={{ color: 'white' }} />
                        ) : (
                          'Send Verification Code'
                        )}
                      </Button>

                      <Box
                        sx={{
                          textAlign: 'center',
                          mt: 3,
                          pt: 3,
                          borderTop: '1px solid #e0e0e0',
                        }}
                      >
                        <Typography variant="body2" sx={{ fontFamily: 'Roboto, sans-serif', color: '#666', fontSize: '14px' }}>
                          Remember your password?{' '}
                          <Link
                            component="button"
                            type="button"
                            onClick={toggleResetPasswordMode}
                            sx={{
                              fontFamily: 'Roboto, sans-serif',
                              color: themeColor,
                              fontWeight: 700,
                              textDecoration: 'none',
                              '&:hover': {
                                textDecoration: 'underline',
                              },
                            }}
                          >
                            Sign In
                          </Link>
                        </Typography>
                      </Box>
                    </Form>
                  )}
                </Formik>
              )}

              {/* New Password Form */}
              {showNewPasswordFields && (
                <Formik
                  initialValues={{ newPassword: '', confirmPassword: '' }}
                  validationSchema={newPasswordSchema}
                  onSubmit={handleNewPasswordSubmit}
                >
                  {({ errors, touched, values }) => (
                    <Form>
                      <TextField
                        fullWidth
                        label="Email Address"
                        value={resetPasswordEmail}
                        disabled
                        sx={{ ...inputFieldStyles, mb: 2 }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <CheckCircleOutline sx={{ color: '#4caf50' }} />
                            </InputAdornment>
                          ),
                        }}
                      />

                      <Alert
                        severity="success"
                        sx={{
                          mb: 3,
                          borderRadius: '12px',
                          border: '1px solid #4caf5020',
                        }}
                      >
                        Email OTP Verified Successfully ✓
                      </Alert>

                      <Field name="newPassword">
                        {({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="New Password"
                            type={showPassword ? 'text' : 'password'}
                            error={touched.newPassword && Boolean(errors.newPassword)}
                            helperText={touched.newPassword && errors.newPassword}
                            onFocus={() => setIsPasswordFieldActive(true)}
                            onBlur={() => setIsPasswordFieldActive(false)}
                            sx={inputFieldStyles}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={togglePassword}
                                    edge="end"
                                    sx={{ color: '#666' }}
                                  >
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      </Field>

                      <PasswordStrengthIndicator
                        password={values.newPassword}
                        isActive={isPasswordFieldActive}
                      />

                      <Field name="confirmPassword">
                        {({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Confirm Password"
                            type={showPassword ? 'text' : 'password'}
                            error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                            helperText={touched.confirmPassword && errors.confirmPassword}
                            sx={inputFieldStyles}
                          />
                        )}
                      </Field>

                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={submitting}
                        sx={primaryButtonSx}
                      >
                        {submitting ? (
                          <CircularProgress size={24} sx={{ color: 'white' }} />
                        ) : (
                          'Reset Password'
                        )}
                      </Button>
                    </Form>
                  )}
                </Formik>
              )}
            </Box>
              </Box>
            </Box>
          </Paper>
        </Fade>

        {/* Footer Text */}
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto, sans-serif',
            textAlign: 'center',
            mt: 3,
            color: 'rgba(255, 255, 255, 0.9)',
            fontSize: '13px',
          }}
        >
          © {new Date().getFullYear()} DigiBooks. All rights reserved.
        </Typography>
      </Container>

      {/* Email OTP Verification Dialog for Password Reset */}
      <EmailVerifyDialog
        open={showPasswordResetOtpDialog}
        onClose={() => setShowPasswordResetOtpDialog(false)}
        email={resetPasswordEmail}
        purpose="password_reset"
        onVerifySuccess={handlePasswordResetOtpVerified}
        title="Verify Your Email"
        description="Please enter the verification code sent to your email to reset your password."
      />
    </Box>
  );
};

export default LoginPage;
